import React, { useCallback, useState, useTransition } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  PhotoIcon,
  XMarkIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import {
  ImageUploadConfig,
  ImageOCRResult,
  OCRProcessingStatus,
  OCREventHandlers,
  DEFAULT_IMAGE_UPLOAD_CONFIG,
} from '../../types';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import logger from '../../utils/logger';

interface ImageUploadProps {
  config?: Partial<ImageUploadConfig>;
  eventHandlers?: OCREventHandlers;
  onResultsChange?: (results: ImageOCRResult[]) => void;
  className?: string;
  disabled?: boolean;
}

/**
 * FL-67: 画像アップロード・OCR処理コンポーネント
 *
 * 機能:
 * - ドラッグ&ドロップによる画像アップロード
 * - 複数ファイル対応（最大5枚）
 * - OCR処理とプログレス表示
 * - 処理結果の表示と管理
 */
export const ImageUpload: React.FC<ImageUploadProps> = ({
  config = {},
  eventHandlers = {},
  onResultsChange,
  className = '',
  disabled = false,
}) => {
  const finalConfig = { ...DEFAULT_IMAGE_UPLOAD_CONFIG, ...config };
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [ocrResults, setOcrResults] = useState<ImageOCRResult[]>([]);
  const [processingFiles, setProcessingFiles] = useState<Set<string>>(new Set());
  const [, startTransition] = useTransition();

  // ファイルドロップ処理
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (disabled) return;

      // ファイル数制限チェック
      const totalFiles = uploadedFiles.length + acceptedFiles.length;
      if (totalFiles > finalConfig.maxFiles) {
        alert(`最大${finalConfig.maxFiles}枚まで選択できます。`);
        return;
      }

      // ファイルサイズチェック
      const oversizedFiles = acceptedFiles.filter(file => file.size > finalConfig.maxSizeBytes);
      if (oversizedFiles.length > 0) {
        const maxSizeMB = Math.round(finalConfig.maxSizeBytes / 1024 / 1024);
        alert(`ファイルサイズは${maxSizeMB}MB以下にしてください。`);
        return;
      }

      // アップロードファイルリストに追加
      const newFiles = [...uploadedFiles, ...acceptedFiles];
      setUploadedFiles(newFiles);

      // 自動処理が有効な場合はOCR処理を開始（非同期で実行）
      if (finalConfig.autoProcess) {
        // React 18のuseTransitionを使用してレンダリング後に処理を実行
        startTransition(() => {
          acceptedFiles.forEach(file => {
            processFileOCR(file);
          });
        });
      }
    },
    [uploadedFiles, finalConfig, disabled]
  );

  // OCR処理実行（リトライ機能付き）
  const processFileOCR = async (file: File, retryCount = 0) => {
    const fileName = file.name;
    const maxRetries = 2;

    // 処理開始状態に設定
    setProcessingFiles(prev => new Set(prev).add(fileName));

    if (retryCount === 0) {
      eventHandlers.onProcessingStart?.(fileName);
    }

    try {
      // 実際のOCR処理を実行
      const { ocrService } = await import('../../services/ocrService');
      const { extractAddressFromText, DEFAULT_ADDRESS_EXTRACTION_OPTIONS } = await import(
        '../../utils/addressExtraction'
      );

      // OCR処理を実行
      const ocrResult = await ocrService.processImage(file, eventHandlers);

      // 住所データを抽出
      logger.info(`[ImageUpload] OCR結果テキスト: "${ocrResult.rawText}"`);
      const extractedAddresses = extractAddressFromText(
        ocrResult.rawText,
        finalConfig.extractionOptions || DEFAULT_ADDRESS_EXTRACTION_OPTIONS
      );
      logger.info(
        `[ImageUpload] 抽出された住所: ${extractedAddresses.length}件`,
        extractedAddresses
      );

      // 結果を完成させる
      const completeResult: ImageOCRResult = {
        ...ocrResult,
        extractedData: extractedAddresses,
      };

      // 結果を状態に追加
      setOcrResults(prev => {
        const newResults = [...prev, completeResult];
        // レンダリング後に非同期でコールバックを実行
        setTimeout(() => onResultsChange?.(newResults), 0);
        return newResults;
      });

      // 住所が抽出された場合のコールバック
      if (extractedAddresses.length > 0) {
        logger.info(`[ImageUpload] onAddressExtracted呼び出し: ${extractedAddresses.length}件`);
        eventHandlers.onAddressExtracted?.(extractedAddresses);
      } else {
        logger.info(`[ImageUpload] 住所が抽出されませんでした`);
      }

      eventHandlers.onProcessingComplete?.(completeResult);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'OCR処理に失敗しました';

      // 初期化エラーの場合はリトライを試行
      if (errorMessage.includes('初期化') && retryCount < maxRetries) {
        logger.info(
          `[ImageUpload] OCR初期化エラー - リトライ ${retryCount + 1}/${maxRetries}: ${fileName}`
        );

        // 少し待ってからリトライ
        setTimeout(
          () => {
            processFileOCR(file, retryCount + 1);
          },
          2000 * (retryCount + 1)
        ); // 2秒、4秒と徐々に間隔を広げる

        return; // 処理完了状態の設定をスキップ
      }

      // エラー結果を作成
      const errorResult: ImageOCRResult = {
        id: `ocr-error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        processedAt: new Date().toISOString(),
        rawText: '',
        confidence: 0,
        extractedData: [],
        processingTimeMs: 0,
        status: OCRProcessingStatus.FAILED,
        errorMessage:
          retryCount > 0 ? `${errorMessage} (${retryCount + 1}回目の試行)` : errorMessage,
      };

      setOcrResults(prev => {
        const newResults = [...prev, errorResult];
        // レンダリング後に非同期でコールバックを実行
        setTimeout(() => onResultsChange?.(newResults), 0);
        return newResults;
      });

      eventHandlers.onProcessingError?.(fileName, errorMessage);
    } finally {
      // 処理完了状態に設定（リトライ時以外は常に実行）
      setProcessingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(fileName);
        return newSet;
      });
    }
  };

  // ファイル削除処理
  const removeFile = (index: number) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(newFiles);

    // 対応するOCR結果も削除
    const fileName = uploadedFiles[index]?.name;
    if (fileName) {
      setOcrResults(prev => {
        const newResults = prev.filter(result => result.fileName !== fileName);
        // レンダリング後に非同期でコールバックを実行
        setTimeout(() => onResultsChange?.(newResults), 0);
        return newResults;
      });
    }
  };

  // OCR結果削除処理
  const removeOCRResult = (resultId: string) => {
    setOcrResults(prev => {
      const newResults = prev.filter(result => result.id !== resultId);
      // レンダリング後に非同期でコールバックを実行
      setTimeout(() => onResultsChange?.(newResults), 0);
      return newResults;
    });
  };

  // react-dropzone設定
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': finalConfig.acceptedTypes.map(type => type.replace('image/', '.')),
    },
    maxFiles: finalConfig.maxFiles,
    maxSize: finalConfig.maxSizeBytes,
    disabled,
  });

  // ファイルサイズ表示用のフォーマッター
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* アップロードエリア */}
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
          ${
            isDragActive
              ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input {...getInputProps()} />
        <PhotoIcon className='h-12 w-12 text-gray-400 mx-auto mb-4' />
        <div className='text-gray-600 dark:text-gray-300'>
          {isDragActive ? (
            <p>ここに画像をドロップしてください</p>
          ) : (
            <div>
              <p className='text-lg font-medium mb-2'>
                画像をドラッグ&ドロップまたはクリックして選択
              </p>
              <p className='text-sm'>
                JPEG, PNG, HEIC対応 | 最大{Math.round(finalConfig.maxSizeBytes / 1024 / 1024)}MB |
                {finalConfig.maxFiles}枚まで
              </p>
            </div>
          )}
        </div>
      </div>

      {/* アップロード済みファイル一覧 */}
      {uploadedFiles.length > 0 && (
        <div className='space-y-2'>
          <h4 className='font-medium text-gray-900 dark:text-gray-100'>
            アップロード済みファイル ({uploadedFiles.length}/{finalConfig.maxFiles})
          </h4>
          <div className='space-y-2'>
            {uploadedFiles.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className='flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg'
              >
                <div className='flex items-center space-x-3'>
                  <PhotoIcon className='h-5 w-5 text-gray-400' />
                  <div>
                    <p className='text-sm font-medium text-gray-900 dark:text-gray-100'>
                      {file.name}
                    </p>
                    <p className='text-xs text-gray-500'>{formatFileSize(file.size)}</p>
                  </div>
                  {processingFiles.has(file.name) && (
                    <div className='flex items-center space-x-2'>
                      <LoadingSpinner size='sm' />
                      <span className='text-xs text-blue-600'>処理中...</span>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => removeFile(index)}
                  className='p-1 text-gray-400 hover:text-red-500 transition-colors'
                  disabled={processingFiles.has(file.name)}
                >
                  <XMarkIcon className='h-4 w-4' />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* OCR処理結果一覧 */}
      {ocrResults.length > 0 && (
        <div className='space-y-2'>
          <h4 className='font-medium text-gray-900 dark:text-gray-100'>
            OCR処理結果 ({ocrResults.length}件)
          </h4>
          <div className='space-y-3'>
            {ocrResults.map(result => (
              <div
                key={result.id}
                className='p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg'
              >
                <div className='flex items-start justify-between mb-2'>
                  <div className='flex items-center space-x-2'>
                    <DocumentTextIcon className='h-5 w-5 text-blue-500' />
                    <span className='font-medium text-gray-900 dark:text-gray-100'>
                      {result.fileName}
                    </span>
                    {result.status === OCRProcessingStatus.COMPLETED && (
                      <CheckCircleIcon className='h-4 w-4 text-green-500' />
                    )}
                    {result.status === OCRProcessingStatus.FAILED && (
                      <ExclamationTriangleIcon className='h-4 w-4 text-red-500' />
                    )}
                  </div>
                  <button
                    onClick={() => removeOCRResult(result.id)}
                    className='p-1 text-gray-400 hover:text-red-500 transition-colors'
                  >
                    <XMarkIcon className='h-4 w-4' />
                  </button>
                </div>

                {result.status === OCRProcessingStatus.COMPLETED && (
                  <div className='space-y-2'>
                    <div className='flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400'>
                      <span>信頼度: {Math.round(result.confidence * 100)}%</span>
                      <span>•</span>
                      <span>処理時間: {result.processingTimeMs}ms</span>
                    </div>
                    <div className='p-2 bg-gray-50 dark:bg-gray-700 rounded text-sm'>
                      <p className='font-medium mb-1'>抽出されたテキスト:</p>
                      <p className='text-gray-700 dark:text-gray-300 whitespace-pre-wrap'>
                        {result.rawText}
                      </p>
                    </div>
                  </div>
                )}

                {result.status === OCRProcessingStatus.FAILED && result.errorMessage && (
                  <div className='p-2 bg-red-50 dark:bg-red-900/20 rounded text-sm text-red-700 dark:text-red-400'>
                    エラー: {result.errorMessage}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
